import {
  convert,
  DayOfWeek,
  Duration,
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  nativeJs,
  Period,
  TemporalAdjusters,
  ZoneId,
  ZoneOffset,
} from '@js-joda/core';
import { clamp } from 'lodash';
import maxBy from 'lodash/maxBy';
import SunCalc from 'suncalc';

import { EnergyReading, TimePeriod } from '../api/energy/types';
import { GroupBy, RequestInterval } from '../api/energyAdvisor/types';
import { LocalizationLanguages } from '../api/user/types';
import { useUser } from '../api/user/useUser';

export const EASY_MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55] as const;

type DateTimeFormatOptions = {
  [s: string]: Intl.DateTimeFormatOptions;
};

export type EasyMinute = (typeof EASY_MINUTES)[number];

export type Meridiem = 'AM' | 'PM';

export type TwentyFourHourTime = {
  hour: number;
  minute: EasyMinute;
};

export type TwelveHourTime = TwentyFourHourTime & {
  meridiem: Meridiem;
};

export enum DateLocale {
  English = 'en-us',
  Spanish = 'es-us',
}

// format options for date with time
export const DateTimeFormat: DateTimeFormatOptions = {
  long: {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }, // October 30, 2020, 2:54 PM
  shortMonth: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }, // Oct 30, 2020, 2:54 PM
  short: {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }, // 10/30/2020, 2:54 PM
  time: { hour: 'numeric', minute: 'numeric' }, // 2:54 PM
};

// format options for date only
export const DateFormat: DateTimeFormatOptions = {
  weekday: { weekday: 'long' }, // Monday
  shortWeekday: { weekday: 'short' }, // Mon
  monthAndYear: { month: 'long', year: 'numeric' }, // June 2020
  shortMonthOnly: { month: 'short' }, // Jun
  shortMonthAndYear: { month: 'short', year: 'numeric' }, // Jun 2020
  year: { year: 'numeric' }, // 2026
  shortDayOfWeek: { weekday: 'short', month: 'numeric', day: 'numeric' }, // Mon 6/23
  shortDayOfMonth: { month: 'short', day: 'numeric', year: 'numeric' }, // Jun 23, 2020
  shortDayOfMonthNoYear: { month: 'short', day: 'numeric' }, // Jun 23
  longDayOfMonth: { month: 'long', day: 'numeric', year: 'numeric' }, // June 23, 2020
  longDayOfMonthNoYear: { month: 'long', day: 'numeric' }, // June 23
  numericShortYear: { month: 'numeric', day: 'numeric', year: '2-digit' }, // 6/23/20
  numericNoYear: { month: 'numeric', day: 'numeric' }, // 6/23
  weekdayMonthAndDay: { weekday: 'long', month: 'long', day: 'numeric' }, // Monday, June 23
  hour: { hour: 'numeric', hour12: true }, // 7 PM
};

// format options for time only
export const TimeFormat: DateTimeFormatOptions = {
  hour: { hour: 'numeric' }, // 5 PM
  hourAndMinute: { hour: 'numeric', minute: '2-digit' }, // 4:53 PM
};

export const parseDate = (date: string | undefined): Date | undefined => (date ? new Date(date) : undefined);

export const parseLocalDateTime = (timestamp: string): LocalDateTime =>
  LocalDateTime.parse(timestamp.length === 10 ? `${timestamp}T00:00` : timestamp);

export const getCurrentHourInTimeZone = (timeZone: string): number =>
  Number(new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false }).format(new Date())) % 24;

// Compact lowercase 12-hour format: "12am", "5pm". For the verbose "5 PM" form, use DateFormat.hour.
export const formatCompactHour = (hour: number): string => {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
};

export const formatDate = (
  date: string | Date,
  format: Intl.DateTimeFormatOptions,
  locale?: LocalizationLanguages,
): string => {
  let formattedDate = new Intl.DateTimeFormat(locale || LocalizationLanguages.English, {
    ...format,
    ...(typeof date === 'string' && date.length === 10 ? { timeZone: 'UTC' } : {}), // set UTC if date is 10 character string
  }).format(new Date(date));

  if (format === DateTimeFormat.long) {
    formattedDate = formattedDate.replace(' at ', ', ');
  }
  return formattedDate;
};

export const useUserLocaleDateFunctions = () => {
  const { user } = useUser();

  const formatLocalDate = (date: LocalDate, format: Intl.DateTimeFormatOptions): string =>
    formatDate(convert(date).toDate(), format, user?.languageCode || LocalizationLanguages.English);

  const formatDateWithUserLocale = (date: string | Date, format: Intl.DateTimeFormatOptions): string =>
    formatDate(date, format, user?.languageCode || LocalizationLanguages.English);

  /**
   * Prints the time since date in human readable form (i.e. 2 days).
   * @param date
   */
  const formatTimeSince = (date: Date | string): string => {
    const NOW_THRESHOLD_IN_SECONDS = 10;
    const TODAY_AT_THRESHOLD_IN_HOURS = 12;

    const duration = Duration.between(LocalDateTime.from(nativeJs(new Date(date))), LocalDateTime.now());
    const days = duration.toDays();
    const hours = duration.toHours();
    const minutes = duration.toMinutes();
    const seconds = minutes / 60;

    if (days > 6) {
      return formatDate(date, DateFormat.longDayOfMonth);
    }

    if (days > 1) {
      return formatDate(date, DateFormat.weekday);
    }

    if (days === 1) {
      return `yesterday`;
    }

    if (hours > TODAY_AT_THRESHOLD_IN_HOURS) {
      return `today at ${formatDate(date, DateTimeFormat.time)}`;
    }

    if (hours > 0) {
      return `${hours} ${hours > 1 ? 'hours' : 'hour'} ago`;
    }

    if (minutes > 0) {
      return `${minutes} ${minutes > 1 ? 'minutes' : 'minute'} ago`;
    }

    if (seconds > NOW_THRESHOLD_IN_SECONDS) {
      return `${seconds} ${seconds > 1 ? 'seconds' : 'second'} ago`;
    }

    return `just now`;
  };

  /**
   * Prints an abbreviated, locale-aware "time ago" string (e.g. "2 mo. ago", "3 days ago").
   * @param date
   */
  const formatRelativeTime = (date: Date | string): string => {
    const locale = user?.languageCode || LocalizationLanguages.English;
    const elapsedSeconds = (new Date(date).getTime() - Date.now()) / 1000;

    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 60 * 60 * 24 * 365],
      ['month', 60 * 60 * 24 * 30],
      ['week', 60 * 60 * 24 * 7],
      ['day', 60 * 60 * 24],
      ['hour', 60 * 60],
      ['minute', 60],
      ['second', 1],
    ];

    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'short' });
    const [unit, secondsInUnit] = units.find(([, seconds]) => Math.abs(elapsedSeconds) >= seconds) ?? ['second', 1];

    return formatter.format(Math.round(elapsedSeconds / secondsInUnit), unit);
  };

  /**
   * Returns a formatted string representing a time window of the specified length starting from the given date.
   *
   * Example outputs:
   * - "7-10 AM" (if both times are in the same AM/PM period)
   * - "11 AM-2 PM" (if the period changes between start and end)
   *
   * @param {string} date - A date string (ISO format recommended) representing the start time.
   * @param {number} windowLength - The length of the time window in hours.
   * @returns {string} A formatted string showing the start and end times (e.g., "7-10 AM" or "11 AM-2 PM").
   */
  const getHourWindowDisplay = (date: string, windowLength: number): string => {
    const start = new Date(date);
    const end = new Date(start);
    end.setHours(start.getHours() + windowLength);

    const startHourWithAmPm = formatDate(start, DateFormat.hour); // e.g., "7 AM"
    const endHourWithAmPm = formatDate(end, DateFormat.hour); // e.g., "10 AM"

    const getAmPm = (timeStr: string) => timeStr.match(/([AaPp])\.?m\.?/i)?.[1].toUpperCase();

    const startAmPm = getAmPm(startHourWithAmPm);
    const endAmPm = getAmPm(endHourWithAmPm);

    const startHour =
      startAmPm === endAmPm ? startHourWithAmPm.replace(/ ?([AaPp])\.?m\.?/i, '') : startHourWithAmPm;

    const hourDisplay = `${startHour}-${endHourWithAmPm}`;
    return hourDisplay; // e.g., "7-10 AM", "10 AM-1 PM"
  };

  /**
   * Returns a formatted string representing a date window between two dates.
   *
   * Example outputs:
   * - "Oct 2 - Oct 15, 2025" (if both dates are in the same year)
   * - "Oct 2, 2024 - Oct 15, 2025" (if the years differ)
   * - "Oct 2, 2025" (if the start and end dates are the same)
   *
   * The formatting automatically respects the user's locale set in the app.
   *
   * @param {LocalDate} startDate - The start date of the window.
   * @param {LocalDate} endDate - The end date of the window.
   * @returns {string} A formatted date window string.
   */
  const getDateWindowDisplay = (startDate: LocalDate, endDate: LocalDate): string => {
    const sameDate = startDate.equals(endDate);
    const sameYear = startDate.year() === endDate.year();

    const shortMonthDay: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const fullDate: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

    const startJsDate = startDate.toJSON();
    const endJsDate = endDate.toJSON();

    if (sameDate) {
      // e.g., "Oct 2, 2025"
      return formatDateWithUserLocale(startJsDate, fullDate);
    }

    if (sameYear) {
      // e.g., "Oct 2 - Oct 15, 2025"
      const startPart = formatDateWithUserLocale(startJsDate, shortMonthDay);
      const endPart = formatDateWithUserLocale(endJsDate, shortMonthDay);
      return `${startPart} - ${endPart}, ${startDate.year()}`;
    }

    // Years differ
    // e.g., "Oct 2, 2024 - Oct 15, 2025"
    const startPart = formatDateWithUserLocale(startJsDate, fullDate);
    const endPart = formatDateWithUserLocale(endJsDate, fullDate);
    return `${startPart} - ${endPart}`;
  };

  return {
    formatDate: formatDateWithUserLocale,
    formatLocalDate,
    formatTimeSince,
    formatRelativeTime,
    getHourWindowDisplay,
    getDateWindowDisplay,
  };
};

const padZero = (num: number): string => {
  return `0${num}`.slice(-2);
};

export const getYearString = (timestamp: string): string => timestamp.slice(0, 0 + 4);
export const getYearMonthString = (timestamp: string): string => timestamp.slice(0, 0 + 7);
export const getYearMonthDayString = (timestamp: string): string => timestamp.slice(0, 0 + 10);
export const getMonthString = (timestamp: string): string => timestamp.slice(5, 5 + 2);
export const getDayString = (timestamp: string): string => timestamp.slice(8, 8 + 2);
export const getHourString = (timestamp: string): string => timestamp.slice(11, 11 + 2);

export const getDaysInMonth = (timestamp: string): number => {
  const year = parseInt(getYearString(timestamp), 10);
  const month = parseInt(getMonthString(timestamp), 10);
  return new Date(year, month, 0).getDate();
};

export const padHoursForDay = (readings: EnergyReading[]): EnergyReading[] => {
  const lastReading: EnergyReading | undefined = maxBy(readings, 'date');
  if (lastReading) {
    const lastHour: number = parseInt(getHourString(lastReading.date), 10);
    const yearMonthDayString: string = getYearMonthDayString(lastReading.date);
    const padding: EnergyReading[] = Array(23 - lastHour)
      .fill(undefined)
      .map((_, i) => ({
        value: 0,
        date: `${yearMonthDayString}T${padZero(lastHour + i + 1)}:00`,
      }));
    return [...readings, ...padding];
  }
  return readings;
};

export const padDaysForMonth = (readings: EnergyReading[]): EnergyReading[] => {
  const lastReading: EnergyReading | undefined = maxBy(readings, 'date');
  if (lastReading) {
    const lastDay: number = parseInt(getDayString(lastReading.date), 10);
    const yearMonthString: string = getYearMonthString(lastReading.date);
    const daysInMonth: number = getDaysInMonth(lastReading.date);
    const padding: EnergyReading[] = Array(daysInMonth - lastDay)
      .fill(undefined)
      .map((_, i) => ({
        value: 0,
        date: `${yearMonthString}-${padZero(lastDay + i + 1)}`,
      }));
    return [...readings, ...padding];
  }
  return readings;
};

export const fillMissingDataPoints = (
  readings: EnergyReading[],
  timePeriod: TimePeriod,
  endDate: LocalDate,
): EnergyReading[] => {
  const expectedTimestamps = getTimestampsForTimePeriod(endDate, timePeriod);

  const readingsMap = new Map<string, EnergyReading>();
  readings.forEach((reading) => {
    readingsMap.set(reading.date, reading);
  });

  const filledReadings: EnergyReading[] = expectedTimestamps.map((timestamp) => {
    const existingReading = readingsMap.get(timestamp);
    if (existingReading) {
      return existingReading;
    } else {
      return {
        date: timestamp,
        value: 0,
      };
    }
  });

  return filledReadings;
};

export const currentYear = new Date().getFullYear();
export const getFirstOfMonth = (date: LocalDate): LocalDate => date.withDayOfMonth(1);
export const getLastOfMonth = (date: LocalDate): LocalDate => date.plusMonths(1).withDayOfMonth(1).minusDays(1);
export const getWeekStart = (date: LocalDate, targetStartDay: DayOfWeek = DayOfWeek.MONDAY): LocalDate =>
  date.with(TemporalAdjusters.previousOrSame(targetStartDay));
export const getWeekEnd = (date: LocalDate, targetEndDay: DayOfWeek = DayOfWeek.SUNDAY): LocalDate =>
  date.with(TemporalAdjusters.nextOrSame(targetEndDay));

export const getDateRangeForTimePeriod = (date: LocalDate, period: TimePeriod): [LocalDate, LocalDate] => {
  switch (period) {
    case TimePeriod.month:
      return [getFirstOfMonth(date), getLastOfMonth(date)];
    case TimePeriod.week:
      return [getWeekStart(date), getWeekEnd(date)];
    default:
      // TimePeriod.day
      return [date, date];
  }
};

export const getTimestampsForTimePeriod = (date: LocalDate, period: TimePeriod): string[] => {
  // hours in day
  if (period === TimePeriod.day) {
    return Array(24)
      .fill(null)
      .map((_, hour) => LocalDateTime.of(date, LocalTime.MIN.plusHours(hour)).toString());
  }

  // days in week or month
  const [start, end] = getDateRangeForTimePeriod(date, period);
  return Array(Period.between(start, end).days() + 1)
    .fill(null)
    .map((_, day) => start.plusDays(day).toString());
};

export const isInDateRange = (date: LocalDate, start: LocalDate, end: LocalDate): boolean =>
  date >= start && date <= end;

export const isInTimeRange = (time: LocalTime, start: LocalTime, end: LocalTime): boolean =>
  time >= start && time <= end;

export const isFutureTimestamp = (timestamp?: string): boolean => {
  if (timestamp?.length === 16) {
    return LocalDateTime.parse(timestamp).isAfter(LocalDateTime.now());
  }
  if (timestamp?.length === 10) {
    return LocalDate.parse(timestamp).isAfter(LocalDate.now());
  }
  return false;
};

/**
 * Returns true if the passed in local date is for a month in past.
 * @param date
 */
export const isMonthInThePast = (date: LocalDate): boolean => {
  const now = LocalDate.now();
  return date < now && !(date.year() === now.year() && date.monthValue() === now.monthValue());
};

export const isWeekInThePast = (date: LocalDate): boolean => {
  const now = LocalDate.now();
  const dateWeekStart = getWeekStart(date);
  const nowWeekStart = getWeekStart(now);
  return dateWeekStart.isBefore(nowWeekStart);
};

export const timestampToLocalDate = (timestamp: string): LocalDate => LocalDate.parse(timestamp.substr(0, 10));

export const dateToLocalDate = (date: Date): LocalDate => LocalDate.from(nativeJs(date));

/**
 * @param utcTimestamp - UTC timestamp string (e.g., "2025-10-13T06:45:00.000000000Z")
 * @param timeZone - IANA timezone string (e.g., "America/New_York")
 * @returns LocalDate in the specified timezone
 */
export const utcTimestampToLocalDate = (utcTimestamp: string, timeZone: string): LocalDate => {
  const date = new Date(utcTimestamp);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const values = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  return LocalDate.of(Number(values['year']), Number(values['month']), Number(values['day']));
};

export const getTrailingDateRange = (numberOfDays: number, timeZone?: string): { from: string; to: string } => {
  const today = utcTimestampToLocalDate(new Date().toISOString(), timeZone || 'UTC');
  return { from: today.minusDays(numberOfDays - 1).toString(), to: today.toString() };
};

export const dateFromLocalDate = (date: LocalDate): Date => convert(date).toDate();

export const dateIsToday = (date: Date | string): boolean => {
  return LocalDate.from(nativeJs(new Date(date))).equals(LocalDate.now());
};

export const dateIsYesterday = (date: Date | string): boolean => {
  const yesterday = LocalDate.now().minusDays(1);
  const providedDate = LocalDate.from(nativeJs(new Date(date)));

  return yesterday.equals(providedDate);
};

export const getJanuaryFirstOfCurrentYear = (): LocalDateTime => {
  const januaryFirst = LocalDateTime.of(currentYear, 1, 1, 0, 0);

  return januaryFirst;
};

export const getSplitDate = (dateString: string): { month: string | undefined; dayAndYear: string } => {
  const dateArray = dateString.split(' ');
  const month = dateArray.shift();
  const dayAndYear = dateArray.join(' ');
  return { month, dayAndYear };
};

/**
 * Converts '2024-02-23' to '2024/02/23' which prevents new Date() from being TZ-aware when parsing.
 * Use this (only) when attempting to strip away any timezone data from a date string and parse it as-is.
 *
 * @param dateString - An ISO date string or simplified date string like '2024-02-23'
 * @returns - A date string like '2024/02/23' that can be passed to the Date constructor
 */
export const dateStringAsSimpleCalendarDay = (dateString = ''): string => {
  return dateString.replaceAll('-', '/').replace(/[TZ].*/, '');
};

export const getClosestEasyMinute = (decimal: number): EasyMinute => {
  const trueMinute = 60 * decimal;
  return [...EASY_MINUTES].sort((a, b) => Math.abs(a - trueMinute) - Math.abs(b - trueMinute))[0];
};

export const clamp24 = (val: number) => clamp(0, val, 24);

const convert24HourTo12Hour = (t: TwentyFourHourTime): TwelveHourTime => {
  const hourTwentyFour = clamp24(t.hour);
  if (hourTwentyFour === 0 || hourTwentyFour === 24) {
    return {
      hour: 12,
      minute: t.minute,
      meridiem: 'AM',
    };
  }
  const meridiem = hourTwentyFour < 12 ? 'AM' : 'PM';
  const hour = hourTwentyFour >= 13 ? hourTwentyFour - 12 : hourTwentyFour;
  return { hour, minute: t.minute, meridiem };
};

export const generate24HourClockTimes = (timesInMinutes: number[]): TwentyFourHourTime[] => {
  const timesDecimal = timesInMinutes.map((t) => t / 60);

  const clockTimes = timesDecimal.map((t): TwentyFourHourTime => {
    const hour = Math.floor(t);
    const minutesDecimal = t - hour;
    const minute = getClosestEasyMinute(minutesDecimal);
    return { hour, minute };
  });
  return clockTimes;
};

export const generate12HourClockTimes = (timesInMinutes: number[]): TwelveHourTime[] => {
  const clockTimesTwentyFour = generate24HourClockTimes(timesInMinutes);
  return clockTimesTwentyFour.map((t) => convert24HourTo12Hour(t));
};

export const timeToString = (t: TwentyFourHourTime, format: '12hr' | '24hr' = '12hr') => {
  const prependZero = (n: number) => (n < 10 ? `0${t.minute}` : t.minute);

  const minutes = prependZero(t.minute);
  const hourTwentyFour = clamp24(t.hour);
  if (format === '24hr') {
    return `${hourTwentyFour}:${minutes}`;
  }
  const twelveHour = convert24HourTo12Hour(t);
  return `${twelveHour.hour}:${minutes} ${twelveHour.meridiem}`;
};

export const areTimesEqual = (a: TwentyFourHourTime, b: TwentyFourHourTime) => {
  return a.hour === b.hour && a.minute === b.minute;
};

export const areDaysEqual = (a: Date, b: Date) => {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

export const convertDateAndTime = (d: Date, t: TwentyFourHourTime): Date => {
  const date = new Date(d);
  date.setHours(t.hour);
  date.setMinutes(t.minute);
  return date;
};

const holidayList = [
  { date: '01/01', name: "New Year's Day" },
  { date: '01/15', name: 'Martin Luther King, Jr. Day' },
  { date: '02/19', name: "President's Day" },
  { date: '05/27', name: 'Memorial Day' },
  { date: '06/19', name: 'Juneteenth' },
  { date: '07/04', name: 'Independence Day' },
  { date: '09/02', name: 'Labor Day' },
  { date: '10/14', name: "Indigenous People's Day" },
  { date: '11/11', name: 'Veterans Day' },
  { date: '11/28', name: 'Thanksgiving Day' },
  { date: '11/29', name: 'Day After Thanksgiving' },
  { date: '12/24', name: 'Christmas Eve' },
  { date: '12/25', name: 'Christmas Day' },
];

export const getPalmettoHolidays = () => {
  const year = new Date().getFullYear();
  return holidayList.map((holiday) => {
    const [month, day] = holiday.date.split('/');
    return {
      date: new Date(year, parseInt(month, 10) - 1, parseInt(day, 10)).toISOString().slice(0, 10),
      name: holiday.name,
    };
  });
};

export const daysFromToday = (date: string): number => {
  const dateObj = new Date(`${date}${!date.includes('T') ? `T00:00:00` : ''}`);
  const today = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()));
  return Math.floor((dateObj.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

export const daysAgo = (input: Date | string): number => {
  const d =
    typeof input === 'string' && input.length === 10
      ? LocalDate.parse(input)
      : LocalDate.from(nativeJs(new Date(input)));

  const today = LocalDate.now();
  const diff = today.toEpochDay() - d.toEpochDay();
  return diff > 0 ? diff : 0;
};

export const getLocalDate = (date: Date): LocalDate => {
  const instant = Instant.parse(date.toISOString());
  const localDate = LocalDate.ofInstant(instant, ZoneId.systemDefault());
  return localDate;
};

export const getYearIntervalFromThisMonth = (groupBy: GroupBy = 'month'): RequestInterval => {
  const today = LocalDate.now();
  const from = today.withDayOfMonth(1);
  const to = from.plusYears(1).plusMonths(1);

  const fromUtc = from.atStartOfDay(ZoneId.systemDefault()).withZoneSameInstant(ZoneOffset.UTC);
  const toUtc = to.atStartOfDay(ZoneId.systemDefault()).withZoneSameInstant(ZoneOffset.UTC);

  return {
    from: fromUtc.toString(),
    to: toUtc.toString(),
    groupBy,
  };
};

export const getIsDaytime = (
  coordinates?: { lat: number; lng: number },
  sunrise?: string,
  sunset?: string,
): boolean => {
  if (coordinates?.lat !== undefined && coordinates?.lng !== undefined) {
    const now = new Date();
    const times = SunCalc.getTimes(now, coordinates.lat, coordinates.lng);
    return now >= times.sunrise && now < times.sunset;
  }

  if (sunrise && sunset) {
    const now = new Date();
    return now >= new Date(sunrise) && now < new Date(sunset);
  }

  return true;
};

export const TIMES_OF_DAY = ['day', 'sunrise', 'sunset', 'night'] as const;
export type TimeOfDay = (typeof TIMES_OF_DAY)[number];

export const getTimeOfDay = (coordinates?: { lat: number; lng: number }): TimeOfDay => {
  if (coordinates?.lat === undefined || coordinates?.lng === undefined) return 'day';

  const now = new Date();
  const times = SunCalc.getTimes(now, coordinates.lat, coordinates.lng);
  const phases = [times.dawn, times.goldenHourEnd, times.sunset, times.dusk];
  if (phases.some((phase) => Number.isNaN(phase.getTime()))) {
    return SunCalc.getPosition(now, coordinates.lat, coordinates.lng).altitude > 0 ? 'day' : 'night';
  }
  if (now >= times.dawn && now < times.goldenHourEnd) return 'sunrise';
  if (now >= times.goldenHourEnd && now < times.sunset) return 'day';
  if (now >= times.sunset && now < times.dusk) return 'sunset';
  return 'night';
};
